import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { SearchFocusType, ReadableSearchTypes } from '@utils/searchUtils'
import { useSearchQuery } from '@src/pages/[country]/[language]/search/[searchTerm]'
import FacetGroupCollapse from '../Facets/FacetGroupCollapse'
import { RadioGroup, FormControlLabel } from '@mui/material'
import RadioButton from '@src/components/RadioButton'
import { buildFacetEventPayload } from '@utils/analytics/facets'
import { sendEvent } from '@utils/analytics/coreAnalytics'

const useStyles = makeStyles()((theme) => ({
  formBody: {
    marginTop: theme.spacing(0.5),
  },
  facet: {
    padding: theme.spacing(1, 0, 0, 2),
    width: 'fit-content',
    '& span': {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  facetFocus: {
    padding: theme.spacing(0, 0, 2, 0),
  },
  facetRadio: {
    padding: theme.spacing(1, 0.5),
    marginRight: theme.spacing(1.5),
    alignSelf: 'flex-start',
  },
}))

interface SearchWithinProps {
  orgId?: string
}

const SearchWithin = ({ orgId }: SearchWithinProps) => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  const [query, , queryActions] = useSearchQuery()
  const formattedOrgId = useMemo(
    () => (orgId?.trim() === 'UCB' ? 'UCB' : orgId?.trim()?.toLowerCase()),
    [orgId]
  )

  const showSearchNavItem = (focus: string): boolean =>
    (query.term && !orgId) || query.focus === focus

  const renderProductsOption = () => {
    if (
      !showSearchNavItem(SearchFocusType.Products) &&
      !showSearchNavItem(SearchFocusType.B2B)
    ) {
      return null
    }

    if (!orgId) {
      return (
        <FormControlLabel
          value={SearchFocusType.Products}
          control={<RadioButton className={classes.facetRadio} />}
          aria-current={
            query.focus === SearchFocusType.Products ? 'page' : undefined
          }
          className={classes.facet}
          checked={query.focus === SearchFocusType.Products}
          label={formatMessage({ id: 'PRODUCTS', defaultMessage: 'Products' })}
          data-testid="srp-nav-products"
        />
      )
    }

    return (
      <FormControlLabel
        disabled
        value={SearchFocusType.Products}
        control={<RadioButton className={classes.facetRadio} />}
        className={classes.facet}
        checked={query.focus === SearchFocusType.B2B}
        label={`${formattedOrgId} ${formatMessage({ id: 'PRODUCTS', defaultMessage: 'Products' })}`}
      />
    )
  }

  const onFocusChange = (value: string) => {
    const builtFacetPayload = buildFacetEventPayload({
      action: 'change search type',
      component: 'left rail',
      filter_category: 'search within',
      filter_name: ReadableSearchTypes[value],
      element_type: 'radio button',
      element_text: ReadableSearchTypes[value],
    })

    sendEvent({ payload: builtFacetPayload })
    queryActions.handleFocusChange(value)
  }

  return (
    <div className={classes.facetFocus}>
      <FacetGroupCollapse defaultCollapse facetKey="FACET_SEARCH_WITHIN">
        <div className={classes.formBody}>
          <RadioGroup
            aria-label="focus-selection"
            onChange={(e) => onFocusChange(e.target.value)}
          >
            {renderProductsOption()}
            {showSearchNavItem(SearchFocusType.SiteContent) && (
              <FormControlLabel
                value={SearchFocusType.SiteContent}
                control={<RadioButton className={classes.facetRadio} />}
                className={classes.facet}
                checked={query.focus === SearchFocusType.SiteContent}
                label={formatMessage({
                  id: 'SITE_CONTENT',
                  defaultMessage: 'Site Content',
                })}
              />
            )}
            {showSearchNavItem(SearchFocusType.TechnicalDocuments) && (
              <FormControlLabel
                value={SearchFocusType.TechnicalDocuments}
                control={<RadioButton className={classes.facetRadio} />}
                aria-current={
                  query.focus === SearchFocusType.TechnicalDocuments
                    ? 'page'
                    : undefined
                }
                className={classes.facet}
                checked={query.focus === SearchFocusType.TechnicalDocuments}
                label={formatMessage({
                  id: 'SUPPORTING_DOCUMENTS',
                  defaultMessage: 'Supporting Documents',
                })}
                data-testid="srp-nav-techdocs"
              />
            )}
          </RadioGroup>
        </div>
      </FacetGroupCollapse>
    </div>
  )
}

export default SearchWithin
